import type { PolygonWithHoles, Triangulation } from '../../geo/types';
import { MIN_TRIANGLE_HEIGHT_M } from '../terrain/degenerate';

function storedKey(x: number, y: number): string {
  return `${Math.fround(x)},${Math.fround(y)}`;
}

/** Earcut and sliver cleanup may omit a ring station bent less than a millimetre. Restore it in the adjacent
 * triangle so differently tiled surfaces retain the same physical boundary profile. */
export function withBoundaryStations(
  input: Triangulation,
  polygon: PolygonWithHoles
): Triangulation {
  const positions = Array.from(input.positions),
    indices = Array.from(input.indices);
  const at = (i: number) => [positions[i * 2]!, positions[i * 2 + 1]!] as const;
  const stored = new Set([...new Set(indices)].map((i) => storedKey(...at(i))));
  let changed = false;
  for (const p of [polygon.outer, ...polygon.holes].flat()) {
    if (stored.has(storedKey(...p))) continue;
    const counts = new Map<string, number>();
    for (let t = 0; t < indices.length; t += 3)
      for (let k = 0; k < 3; k++) {
        const a = indices[t + k]!,
          b = indices[t + ((k + 1) % 3)]!,
          key = a < b ? `${a}:${b}` : `${b}:${a}`;
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    let found = false;
    for (let t = 0; t < indices.length && !found; t += 3)
      for (let k = 0; k < 3; k++) {
        const a = indices[t + k]!,
          b = indices[t + ((k + 1) % 3)]!,
          c = indices[t + ((k + 2) % 3)]!,
          key = a < b ? `${a}:${b}` : `${b}:${a}`;
        if (counts.get(key) !== 1) continue;
        const q = at(a),
          r = at(b),
          dx = r[0] - q[0],
          dy = r[1] - q[1],
          lengthSq = dx * dx + dy * dy;
        if (!lengthSq) continue;
        const share = ((p[0] - q[0]) * dx + (p[1] - q[1]) * dy) / lengthSq,
          error = Math.abs((p[0] - q[0]) * dy - (p[1] - q[1]) * dx);
        if (
          share <= 1e-10 ||
          share >= 1 - 1e-10 ||
          error > Math.sqrt(lengthSq) * MIN_TRIANGLE_HEIGHT_M
        )
          continue;
        const v = positions.length / 2;
        positions.push(p[0], p[1]);
        stored.add(storedKey(...p));
        indices.splice(t, 3, a, v, c);
        indices.push(v, b, c);
        found = changed = true;
        break;
      }
  }
  return changed
    ? { positions: Float64Array.from(positions), indices: Uint32Array.from(indices) }
    : input;
}
